import { useState, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CUSTOMER_STATUS_OPTS } from "@/lib/constants";
import { Loader2 } from "lucide-react";

const EMPTY = {
  type: "private", first_name: "", last_name: "", company_name: "", date_of_birth: "",
  street: "", house_number: "", postal_code: "", city: "", country: "Deutschland",
  email: "", phone: "", mobile: "", language: "de", status: "aktiv", notes: "",
  consent_marketing: false, consent_whatsapp: false,
};

export function CustomerForm({ open, onOpenChange, customer, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!customer;

  useEffect(() => {
    if (open) setForm(customer ? { ...EMPTY, ...customer } : EMPTY);
  }, [open, customer]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) await api.put(`/customers/${customer.id}`, form);
      else await api.post("/customers", form);
      toast.success(isEdit ? "Kunde aktualisiert" : "Kunde angelegt");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>{isEdit ? "Kunde bearbeiten" : "Neuer Kunde"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Kundentyp</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="customer-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Privatkunde</SelectItem>
                  <SelectItem value="business">Geschäftskunde</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="customer-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CUSTOMER_STATUS_OPTS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {form.type === "business" ? (
            <div className="space-y-1.5"><Label>Firmenname</Label>
              <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="customer-company-input" /></div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Vorname</Label><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="customer-firstname-input" /></div>
              <div className="space-y-1.5"><Label>Nachname</Label><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="customer-lastname-input" /></div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5"><Label>Straße</Label><Input value={form.street} onChange={(e) => set("street", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Nr.</Label><Input value={form.house_number} onChange={(e) => set("house_number", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>PLZ</Label><Input inputMode="numeric" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} data-testid="customer-plz-input" /></div>
            <div className="col-span-2 space-y-1.5"><Label>Ort</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>E-Mail</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="customer-email-input" /></div>
            <div className="space-y-1.5"><Label>Telefon</Label><Input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="customer-phone-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Mobil</Label><Input type="tel" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Sprache</Label>
              <Select value={form.language} onValueChange={(v) => set("language", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="de">Deutsch</SelectItem><SelectItem value="tr">Türkisch</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Interne Notizen</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
            <span className="text-sm">Einwilligung Marketing</span>
            <Switch checked={form.consent_marketing} onCheckedChange={(v) => set("consent_marketing", v)} data-testid="consent-marketing-switch" />
          </div>
          <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
            <span className="text-sm">Einwilligung WhatsApp</span>
            <Switch checked={form.consent_whatsapp} onCheckedChange={(v) => set("consent_whatsapp", v)} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" disabled={saving} data-testid="customer-save-button">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Speichern
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
